<script lang="ts">
  import Icon from './Icon.svelte';
  import { computePosition, flip, shift, offset, autoUpdate } from '@floating-ui/dom';
  import {
    projectCellPopoverState,
    enterProjectCellPopover,
    leaveProjectCellPopover,
    hideProjectCellPopover,
  } from '../lib/stores/projectCellPopover';
  import { hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { openProjectRail, openProjectModalForEdit, draggedProject } from '../lib/stores/ui';
  import { setCompactProjectDragImage } from '../lib/dragImage';
  import { router } from '@inertiajs/svelte';
  import { categoryIcons, statusAccentColors, progressStyle, progressComplete } from '../lib/utils';
  import type { Project } from '../lib/types';
  import ResponsiveOverlay from './ResponsiveOverlay.svelte';
  import { viewport } from '../lib/stores/viewport';

  const STATUS_CONFIG: Record<string, { label: string; dotColor: string }> = {
    planned: { label: 'Planned', dotColor: '#3b82f6' },
    active: { label: 'Active', dotColor: '#d97706' },
    almost: { label: 'Almost', dotColor: '#ea580c' },
    done: { label: 'Done', dotColor: '#16a34a' },
  };

  let popoverState = $derived($projectCellPopoverState);

  let floatingEl: HTMLDivElement | undefined = $state();
  let floatingStyle = $state('position: fixed; left: 0; top: 0; visibility: hidden;');
  let autoUpdateCleanup: (() => void) | null = null;

  async function updatePosition(anchorEl: HTMLElement, floating: HTMLDivElement) {
    const result = await computePosition(anchorEl, floating, {
      placement: 'bottom-start',
      middleware: [
        offset(6),
        flip({
          fallbackPlacements: ['top-start', 'bottom-end', 'top-end', 'right-start', 'left-start'],
        }),
        shift({ padding: 8 }),
      ],
    });
    floatingStyle = `position: fixed; left: ${result.x}px; top: ${result.y}px; width: 280px; max-height: 380px; visibility: visible;`;
  }

  // eslint-disable-next-line momentium/no-unguarded-effect -- Floating UI autoUpdate lifecycle: must call autoUpdate() on mount and return cleanup
  $effect(() => {
    if (autoUpdateCleanup) {
      autoUpdateCleanup();
      autoUpdateCleanup = null;
    }

    if (popoverState && floatingEl) {
      const anchorEl = popoverState.anchorEl;
      autoUpdateCleanup = autoUpdate(anchorEl, floatingEl, () => {
        if (floatingEl) updatePosition(anchorEl, floatingEl);
      });
    }

    return () => {
      if (autoUpdateCleanup) {
        autoUpdateCleanup();
        autoUpdateCleanup = null;
      }
    };
  });

  function handleProjectClick(projectId: string) {
    hideProjectCellPopover(true);
    hideProjectTooltip(true);
    openProjectRail(projectId);
  }

  function handleEditProject(e: MouseEvent, project: Project) {
    e.stopPropagation();
    hideProjectCellPopover(true);
    hideProjectTooltip(true);
    openProjectModalForEdit(project);
  }

  function handleFullPage(e: MouseEvent, projectId: string) {
    e.stopPropagation();
    hideProjectCellPopover(true);
    hideProjectTooltip(true);
    router.visit(`/projects/${projectId}`);
  }

  function handleDragStart(e: DragEvent, project: Project) {
    hideProjectTooltip(true);
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/project', String(project.id));
      setCompactProjectDragImage(e, project);
    }
    // Defer popover close so the browser captures the drag image before DOM removal
    requestAnimationFrame(() => {
      hideProjectCellPopover(true);
    });
  }

  function handleDragEnd() {
    draggedProject.set(null);
  }
</script>

<!-- Desktop: hover card anchored to the "+N more" chip. Mobile: half-height bottom sheet. -->
<ResponsiveOverlay
  open={!!popoverState}
  onClose={() => hideProjectCellPopover(true)}
  detents={['half', 'full']}
  initialDetent="half"
  title={popoverState?.label}
  label="Projects"
>
  {#snippet desktop(content)}
    {#if popoverState}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        class="project-popover"
        style={floatingStyle}
        bind:this={floatingEl}
        onmouseenter={enterProjectCellPopover}
        onmouseleave={leaveProjectCellPopover}
      >
        <div class="popover-header">
          <div class="popover-label">{popoverState.label}</div>
          <span class="popover-count">{popoverState.projects.length}</span>
        </div>
        {@render content()}
      </div>
    {/if}
  {/snippet}

  {#if popoverState}
    {@const { projects } = popoverState}
    <div class="popover-projects">
      {#each projects as project (project.id)}
        {@const statusCfg = STATUS_CONFIG[project.status]}
        <div class="popover-project-row">
          <button
            type="button"
            class="popover-project {statusAccentColors[project.status]}"
            style={progressStyle(project)}
            data-progress-complete={progressComplete(project) || undefined}
            draggable={!$viewport.isMobile}
            ondragstart={(e) => handleDragStart(e, project)}
            ondragend={handleDragEnd}
            onclick={() => handleProjectClick(project.id)}
          >
            <span class="project-category">{categoryIcons[project.category]}</span>
            {#if project.isPriority}
              <span class="project-star">★</span>
            {/if}
            <span class="project-title">{project.title}</span>
            <span class="project-status">
              <span class="status-dot" style="background: {statusCfg.dotColor};"></span>
            </span>
            {#if project.multiWeek && project.totalWeeks}
              <span class="project-span">{project.totalWeeks}w</span>
            {/if}
          </button>
          <div class="project-actions">
            <button
              type="button"
              class="action-btn hover-reveal"
              title="Edit"
              onclick={(e) => handleEditProject(e, project)}
              ><Icon name="edit" size={$viewport.isMobile ? 16 : 11} label="Edit" /></button
            >
            <button
              type="button"
              class="action-btn hover-reveal"
              title="Full page"
              onclick={(e) => handleFullPage(e, project.id)}
              ><Icon name="open" size={$viewport.isMobile ? 16 : 11} label="Full page" /></button
            >
          </div>
        </div>
      {/each}
    </div>
  {/if}
</ResponsiveOverlay>

<style>
  .project-popover {
    z-index: 200;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.15),
      0 4px 10px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    pointer-events: auto;
  }

  .popover-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
  }

  .popover-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #1f2937;
  }

  .popover-count {
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    background: #e5e7eb;
    padding: 0.0625rem 0.4375rem;
    border-radius: 9999px;
    min-width: 1.125rem;
    text-align: center;
  }

  .popover-projects {
    flex: 1;
    overflow-y: auto;
    padding: 0.375rem;
    display: flex;
    flex-direction: column;
    gap: 0.1875rem;
  }

  .popover-project-row {
    display: flex;
    align-items: center;
    gap: 0.125rem;
  }

  .popover-project {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.3125rem 0.375rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    border-radius: 0.25rem;
    cursor: grab;
    transition:
      background 0.1s,
      border-color 0.1s;
    text-align: left;
    min-width: 0;
    flex: 1;
  }

  .popover-project:active {
    cursor: grabbing;
  }

  .popover-project:hover {
    background: #f0f9ff;
    border-color: #bfdbfe;
  }

  .project-actions {
    display: flex;
    align-items: center;
    gap: 0;
    flex-shrink: 0;
  }

  .action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.5625rem;
    background: none;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.1s;
    padding: 0;
    line-height: 1;
    opacity: 0.35;
  }

  .popover-project-row:hover .action-btn {
    opacity: 0.7;
  }

  .action-btn:hover {
    opacity: 1 !important;
    background: #eff6ff;
  }

  .project-category {
    font-size: 0.625rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .project-star {
    color: #facc15;
    font-size: 0.625rem;
    flex-shrink: 0;
  }

  .project-title {
    font-size: 0.75rem;
    font-weight: 500;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    flex: 1;
  }

  .project-status {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .project-span {
    flex-shrink: 0;
    font-size: 0.5625rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  /* Mobile: the same content in a bottom sheet (ResponsiveOverlay). */
  :global(.mobile-sheet-panel) .popover-projects {
    padding: 0.5rem 0.75rem;
    gap: 0.375rem;
  }

  :global(.mobile-sheet-panel) .popover-project {
    min-height: var(--mobile-tap);
    padding: 0.5rem 0.625rem;
    gap: 0.375rem;
    cursor: pointer;
  }

  :global(.mobile-sheet-panel) .project-title {
    font-size: 0.9375rem;
  }

  :global(.mobile-sheet-panel) .project-category,
  :global(.mobile-sheet-panel) .project-star {
    font-size: 0.875rem;
  }

  :global(.mobile-sheet-panel) .project-span {
    font-size: 0.75rem;
  }

  :global(.mobile-sheet-panel) .action-btn {
    width: var(--mobile-tap);
    height: var(--mobile-tap);
    opacity: 0.7;
  }
</style>
